// Procedural WebAudio: footsteps (and a landing thud) with no sound assets —
// a low heel thump plus a band-passed scuff, pitch/level jittered per step so
// a walk never sounds like a metronome. Volume is the stealth model: sprint is
// loud, run normal, walk quiet, Ctrl-sneak near-silent (see controller).
export class GameAudio {
  constructor() {
    this.ctx = null;
    this.master = null;
  }

  // AudioContext needs a user gesture; call from the pointer-lock click.
  ensure() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.9;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
  }

  // One footfall. vol 0..1; heavier stances pass a lower `tone`.
  // `surface` (M11) picks the sound: with the CC0 sample library loaded
  // (this.sfx, set in main) each surface plays a sliced window of a real
  // recording — forest adds a twig/leaf-crunch layer on top of grass, exactly
  // the "kviste der knaser" feel. Without samples: the procedural fallback.
  step(vol = 0.5, tone = 1, surface = 'dirt') {
    if (!this.ctx || this.ctx.state !== 'running' || vol <= 0.01) return;
    if (this.sfx) {
      const role = surface === 'forest' ? 'step.grass' : `step.${surface}`;
      const opts = { vol: vol * 0.85, rate: tone, jitter: 0.1, slice: 0.4 };
      if (this.sfx.play(role, opts) || this.sfx.play('step.dirt', opts)) {
        if (surface === 'forest' && Math.random() < 0.45) {
          this.sfx.play('step.twigs', { vol: vol * 0.5, jitter: 0.15, slice: 0.35, delay: 0.02 });
        }
        return;
      }
    }
    const SURF = {
      grass:   { freq: 520, spread: 320, q: 0.6, durMul: 1.4, scuff: 0.34, thump: 0.5 },
      forest:  { freq: 620, spread: 500, q: 0.7, durMul: 1.3, scuff: 0.36, thump: 0.5 },
      dirt:    { freq: 750, spread: 650, q: 0.8, durMul: 1.0, scuff: 0.3, thump: 0.55 },
      asphalt: { freq: 1500, spread: 900, q: 1.6, durMul: 0.6, scuff: 0.34, thump: 0.5 },
      wood:    { freq: 1100, spread: 500, q: 1.2, durMul: 0.8, scuff: 0.3, thump: 0.6 },
      metal:   { freq: 1900, spread: 700, q: 2.2, durMul: 0.55, scuff: 0.3, thump: 0.5 },
      snow:    { freq: 420, spread: 220, q: 0.5, durMul: 1.7, scuff: 0.42, thump: 0.35 },
      sand:    { freq: 380, spread: 180, q: 0.5, durMul: 1.8, scuff: 0.38, thump: 0.35 },
    };
    const S = SURF[surface] || SURF.dirt;
    const c = this.ctx, t = c.currentTime;
    const jit = 0.85 + Math.random() * 0.3;

    // heel thump
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime((85 + Math.random() * 25) * tone, t);
    o.frequency.exponentialRampToValueAtTime(38 * tone, t + 0.085);
    g.gain.setValueAtTime(vol * S.thump * jit, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.11);
    o.connect(g); g.connect(this.master);
    o.start(t); o.stop(t + 0.13);

    // sole scuff (short noise burst through a bandpass)
    const dur = (0.055 + Math.random() * 0.05) * S.durMul;
    const buf = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = c.createBufferSource();
    src.buffer = buf;
    const f = c.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = (S.freq + Math.random() * S.spread) * tone;
    f.Q.value = S.q;
    const g2 = c.createGain();
    g2.gain.value = vol * S.scuff * jit;
    src.connect(f); f.connect(g2); g2.connect(this.master);
    src.start(t + 0.008);
  }

  // Landing after a jump: heavier double-thump.
  land(vol = 0.7) {
    this.step(vol, 0.75);
    setTimeout(() => this.step(vol * 0.5, 0.85), 70);
  }
}
